import React, { useState, useRef, useEffect } from "react";
import { isMobileScreen, truncateString } from "../lib/utils";
import { RiArrowDropDownLine } from "react-icons/ri";
import { DropdownMenu } from "../@/components/ui/dropdown-menu";
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@radix-ui/react-dropdown-menu";
import { ScrollArea } from "../@/components/ui/scroll-area";
import { IoCloseOutline } from "react-icons/io5";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "../@/components/ui/dialog";
import { IoMdClose } from "react-icons/io";

interface DropdownOption {
  value: string;
  label: string;
}

interface GenericDropdownProps {
  labelValue: string | undefined;
  onChange: (value: string) => void;
  options: DropdownOption[];
  placeholder?: string;
  label?: string;
  error: boolean;
  isDisabled?: boolean;
  className?: string;
  showAllOption?: boolean;
  scrollHeight?: string;
  dropdownMenuContentWidth?: string;
}

const GenericDropdown: React.FC<GenericDropdownProps> = ({
  labelValue,
  onChange,
  options,
  placeholder,
  label,
  error,
  isDisabled = false,
  showAllOption = false,
  scrollHeight = "h-60",
  dropdownMenuContentWidth = "w-56",
  className = "",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const truncateNumber = isMobileScreen ? 40 : 22;

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchTerm]);

  useEffect(() => {
    if (isMobileScreen && isOpen) {
      // Function to handle keyboard showing
      const handleResize = () => {
        // Calculate keyboard height by comparing window inner height with screen height
        const visualViewportHeight =
          window.visualViewport?.height || window.innerHeight;
        const screenHeight = window.screen.height;
        const estimatedKeyboardHeight = screenHeight - visualViewportHeight;

        if (estimatedKeyboardHeight > 100) {
          // Threshold to detect keyboard
          setKeyboardHeight(estimatedKeyboardHeight);
        } else {
          setKeyboardHeight(0);
        }
      };

      // Add event listeners
      window.visualViewport?.addEventListener("resize", handleResize);
      window.addEventListener("resize", handleResize);

      // Initial check
      handleResize();

      return () => {
        window.visualViewport?.removeEventListener("resize", handleResize);
        window.addEventListener("resize", handleResize);
      };
    }
  }, [isMobileScreen, isOpen]);

  return isMobileScreen ? (
    // Mobile view with Dialog
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <div className="relative flex items-center justify-center gap-2">
        <DialogTrigger
          className={`border flex gap-2 justify-between align-center ${
            error ? "border-red-500" : "border-gray-300"
          } rounded-lg py-[0.5rem] pl-3 pr-1 cursor-pointer ${className} justify-between ${
            isDisabled ? "opacity-50 cursor-not-allowed" : ""
          }`}
          disabled={isDisabled}
        >
          <span className={`mr-3 ${!labelValue ? "text-customGrayText" : ""}`}>
            {labelValue ? truncateString(labelValue, 14) : placeholder}
          </span>
          <RiArrowDropDownLine
            className={`h-6 w-6 ${!labelValue ? "text-customGrayText" : ""}`}
          />
        </DialogTrigger>

        {labelValue && !isDisabled && (
          <IoCloseOutline
            className="h-5 w-5 cursor-pointer hover:text-red-500 absolute right-6"
            onClick={() => onChange("-1")}
          />
        )}
      </div>

      <DialogContent className="w-full max-w-full sm:max-w-md p-0 h-[90vh] flex flex-col z-[10000]">
        <div className="sticky top-0 z-10 bg-white p-4 border-b flex items-center justify-between">
          <input
            type="text"
            ref={inputRef}
            className="w-full px-2 py-1 border border-gray-300 rounded-md mr-2"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
            onFocus={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
          <button
            className="p-2 rounded-full hover:bg-gray-100"
            onClick={() => setIsOpen(false)}
          >
            <IoMdClose className="h-6 w-6" />
          </button>
        </div>

        <div
          className="overflow-y-auto flex-1 -webkit-overflow-scrolling: touch"
          style={{
            paddingBottom: keyboardHeight ? `${keyboardHeight + 20}px` : "0px",
          }}
        >
          {label && <div className="text-gray-700 p-4 pb-2">{label}</div>}
          {filteredOptions.length > 0 ? (
            <div className="px-4">
              {filteredOptions.map((option, index) => (
                <div
                  key={`${option.value + index}`}
                  className="p-3 cursor-pointer hover:bg-gray-100 text-gray-700 border-b last:border-b-0"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                >
                  {truncateString(option.label, truncateNumber)}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 text-gray-500">No options found</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  ) : (
    // Desktop view with DropdownMenu
    <DropdownMenu>
      <div className="relative flex items-center justify-center gap-2 ">
        <DropdownMenuTrigger
          className={`border flex gap-2 justify-between align-center   ${
            error ? "border-red-500" : "border-gray-300"
          } rounded-lg py-[0.5rem] pl-3 pr-1 cursor-pointer ${className} justify-between ${
            isDisabled ? "opacity-50 cursor-not-allowed" : ""
          }`}
          disabled={isDisabled}
        >
          <span className={`mr-3 ${!labelValue ? "text-customGrayText" : ""}`}>
            {labelValue ? truncateString(labelValue, 14) : placeholder}
          </span>
          <RiArrowDropDownLine
            className={`h-6 w-6 ${!labelValue ? "text-customGrayText" : ""}`}
          />
        </DropdownMenuTrigger>

        {labelValue && !isDisabled && (
          <IoCloseOutline
            className="h-5 w-5 cursor-pointer hover:text-red-500 absolute right-6"
            onClick={() => onChange("-1")}
          />
        )}
      </div>

      <DropdownMenuContent
        className={`z-[10000] overflow-hidden max-h-80 bg-white rounded-md border ${dropdownMenuContentWidth}`}
        onEscapeKeyDown={(event) => {}}
        onPointerDownOutside={(event) => {}}
      >
        <div className="hover:border-none">
          <div className="p-2 hover:border-none">
            <input
              type="text"
              ref={inputRef}
              className="w-full px-2 py-1 border border-gray-300 rounded-md"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
              onFocus={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
          <ScrollArea className={`${scrollHeight} rounded-md border bg-white`}>
            <div className="p-2 hover:border-none">
              {label && <div className="text-gray-700 mb-1">{label}</div>}
              {/* Filtered Options */}
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => (
                  <DropdownMenuItem
                    key={`${option.value + index}`}
                    className="p-2 cursor-pointer hover:bg-gray-100 text-gray-700 opacity-50 hover:opacity-100"
                    onClick={() => {
                      onChange(option.value);
                    }}
                  >
                    {truncateString(option.label, truncateNumber)}
                  </DropdownMenuItem>
                ))
              ) : (
                <div className="p-2 text-gray-500">No options found</div>
              )}
            </div>
          </ScrollArea>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default GenericDropdown;
